import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  FaTachometerAlt, FaEnvelope, FaCogs, FaUsers,
  FaStar, FaBriefcase, FaSignOutAlt, FaTimes
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { FaShieldAlt } from "react-icons/fa";

const navItems = [
  { to: "/admin/dashboard", icon: <FaTachometerAlt />, label: "Dashboard", color: "#0ea5e9" },
  { to: "/admin/contacts", icon: <FaEnvelope />, label: "Contacts", color: "#8b5cf6" },
  { to: "/admin/services", icon: <FaCogs />, label: "Services", color: "#10b981" },
  { to: "/admin/portfolio", icon: <FaBriefcase />, label: "Portfolio", color: "#f59e0b" },
  { to: "/admin/team", icon: <FaUsers />, label: "Team", color: "#ef4444" },
  { to: "/admin/testimonials", icon: <FaStar />, label: "Testimonials", color: "#06b6d4" },
];

export default function AdminSidebar({ open, setOpen, onLogout }) {
  const navigate = useNavigate();

  const logout = () => {
    setOpen(false);
    onLogout();
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-30 lg:hidden backdrop-blur-sm" onClick={() => setOpen(false)} />
        )}
      </AnimatePresence>

      <aside className={`fixed top-0 left-0 h-full w-64 z-40 flex flex-col transition-transform duration-300
        ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        style={{ background: "rgba(6,11,24,0.98)", borderRight: "1px solid rgba(255,255,255,0.06)" }}>

        {/* Top gradient line */}
        <div className="h-0.5 w-full gradient-bg" />

        {/* Logo */}
        <div className="flex items-center justify-between px-5 py-5 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 gradient-bg rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20 flex-shrink-0">
              <img src="/DreamGoldLogo.png" alt="logo" className="w-8 h-8 object-cover rounded-lg" />
            </div>
            <div>
              <h2 className="font-black text-sm tracking-tight">
                <span className="text-white">ONE</span><span className="gradient-text">XVORA</span>
              </h2>
              <p className="text-gray-600 text-xs">Admin Panel</p>
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="lg:hidden text-gray-500 hover:text-white transition-colors">
            <FaTimes className="text-sm" />
          </button>
        </div>

        {/* Nav label */}
        <div className="px-5 pt-5 pb-2">
          <span className="text-gray-600 text-xs font-semibold uppercase tracking-widest">Navigation</span>
        </div>

        {/* Nav items */}
        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto pb-4">
          {navItems.map((item, i) => (
            <NavLink key={item.to} to={item.to} onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 group relative
                ${isActive ? "text-white" : "text-gray-500 hover:text-white hover:bg-white/5"}`
              }>
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.div layoutId="activeNav" className="absolute inset-0 rounded-xl"
                      style={{ background: `${item.color}15`, border: `1px solid ${item.color}30` }}
                      transition={{ type: "spring", stiffness: 300, damping: 30 }} />
                  )}
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm relative z-10 flex-shrink-0 transition-all"
                    style={isActive ? { background: `${item.color}20`, color: item.color } : {}}>
                    {item.icon}
                  </div>
                  <span className="relative z-10">{item.label}</span>
                  {isActive && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full relative z-10" style={{ background: item.color }} />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User + Logout */}
        <div className="p-3 border-t border-white/5">
          <button onClick={logout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-all w-full group">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-white/5 group-hover:bg-red-500/10 transition-all">
              <FaSignOutAlt className="text-sm" />
            </div>
            Logout
          </button>
        </div>
      </aside>
    </>
  );
}
